import { t } from "@/lib/i18n/interpolate";
import {
  type Decimal,
  decimalToString,
  parseDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  CONVENTIONS,
  type Convention,
  conventionsAgree,
  type DivisionResult,
  type ModOperation,
  type ModularResult,
  modularOperation,
  remainderOf,
  remainderText,
} from "@/lib/tool-kit/arithmetic/modulo";
import { parseBigIntToken } from "@/lib/tool-kit/math/bigint";

/** What the modulo calculator shows in its remainder and modular-arithmetic modes. */
export interface RemainderView {
  a: Decimal;
  n: Decimal;
  aText: string;
  nText: string;
  results: DivisionResult[];
  agree: boolean;
  divisible: boolean;
}

export type RemainderComputation =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "zero-modulus" }
  | { kind: "ok"; view: RemainderView };

export function computeRemainder(
  rawA: string,
  rawN: string
): RemainderComputation {
  if (rawA.trim() === "" || rawN.trim() === "") {
    return { kind: "empty" };
  }
  const a = parseDecimal(rawA);
  const n = parseDecimal(rawN);
  if (a === null || n === null) {
    return { kind: "invalid" };
  }
  const results: DivisionResult[] = [];
  for (const convention of CONVENTIONS) {
    const result = remainderOf(a, n, convention);
    if (result === null) {
      return { kind: "zero-modulus" };
    }
    results.push(result);
  }
  return {
    kind: "ok",
    view: {
      a,
      n,
      aText: decimalToString(a),
      nText: decimalToString(n),
      results,
      agree: conventionsAgree(results),
      divisible: results[0].remainderScaled === BigInt(0),
    },
  };
}

export function resultFor(
  view: RemainderView,
  convention: Convention
): DivisionResult {
  return (
    view.results.find((r) => r.convention === convention) ?? view.results[0]
  );
}

export function checkLine(view: RemainderView, result: DivisionResult): string {
  const q = result.quotient.toString().replace("-", "−");
  const n = view.nText;
  const wrapN = n.startsWith("−") ? `(${n})` : n;
  const wrapQ = q.startsWith("−") ? `(${q})` : q;
  const r = remainderText(result);
  const wrapR = r.startsWith("−") ? `(${r})` : r;
  return `${wrapQ} × ${wrapN} + ${wrapR} = ${view.aText}`;
}

/* ------------------------------------------------------ modular operations */

export interface OperationsView {
  result: ModularResult;
}

export type OperationsComputation =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "bad-modulus" }
  | { kind: "negative-exponent" }
  | { kind: "too-big" }
  | { kind: "ok"; view: OperationsView };

const MAX_OPERAND_DIGITS = 400;

export function computeOperations(
  operation: ModOperation,
  rawA: string,
  rawB: string,
  rawN: string
): OperationsComputation {
  if ([rawA, rawB, rawN].some((raw) => raw.trim() === "")) {
    return { kind: "empty" };
  }
  const a = parseBigIntToken(rawA.replace(SPACES, ""));
  const b = parseBigIntToken(rawB.replace(SPACES, ""));
  const n = parseBigIntToken(rawN.replace(SPACES, ""));
  if (a === null || b === null || n === null) {
    return { kind: "invalid" };
  }
  if ([rawA, rawB, rawN].some((raw) => raw.length > MAX_OPERAND_DIGITS)) {
    return { kind: "too-big" };
  }
  if (n < BigInt(2)) {
    return { kind: "bad-modulus" };
  }
  if (operation === "power" && b < BigInt(0)) {
    return { kind: "negative-exponent" };
  }
  const result = modularOperation(operation, a, b, n);
  if (result === null) {
    return { kind: "bad-modulus" };
  }
  return { kind: "ok", view: { result } };
}

const SPACES = /\s+/g;

export interface OperationWords {
  reduceFirst: string;
  rawLine: string;
  powerLine: string;
  symbols: Record<ModOperation, string>;
}

function neg(value: bigint): string {
  return value < BigInt(0) ? `(−${(-value).toString()})` : value.toString();
}

export function operationLines(
  view: OperationsView,
  words: OperationWords
): string[] {
  const { result } = view;
  const symbol = words.symbols[result.operation];
  const lines: string[] = [];
  if (result.operation === "power") {
    lines.push(
      t(words.powerLine, {
        a: neg(result.a),
        b: result.b.toString(),
        n: result.n.toString(),
        result: result.result.toString(),
      })
    );
    return lines;
  }
  lines.push(
    t(words.rawLine, {
      a: neg(result.a),
      op: symbol,
      b: neg(result.b),
      raw: neg(result.raw ?? BigInt(0)),
      n: result.n.toString(),
      result: result.result.toString(),
    })
  );
  lines.push(
    t(words.reduceFirst, {
      a: neg(result.a),
      b: neg(result.b),
      n: result.n.toString(),
      aR: result.aReduced.toString(),
      bR: result.bReduced.toString(),
      op: symbol,
      result: result.result.toString(),
    })
  );
  return lines;
}
